import { useCallback } from 'react'
import { DocumentIcon, FolderIcon } from './icons'
import { FileTreeNode } from './fileTreeUtils'

interface FileTreeItemProps {
  node: FileTreeNode
  level: number
  selectedPath: string | null
  onFileClick: (path: string, data?: unknown) => void
  onFolderToggle: (path: string) => void
}

export function FileTreeItem({
  node,
  level,
  selectedPath,
  onFileClick,
  onFolderToggle
}: FileTreeItemProps) {
  const isSelected = node.type === 'file' && node.path === selectedPath
  const isFolder = node.type === 'folder'
  const isExpanded = node.isExpanded ?? false

  const handleClick = useCallback(() => {
    if (isFolder) {
      onFolderToggle(node.path)
    } else {
      onFileClick(node.path, node.data)
    }
  }, [isFolder, node.path, node.data, onFileClick, onFolderToggle])

  // Calculate padding based on nesting level
  const paddingLeft = level === 0 ? 0 : level * 32

  return (
    <>
      <div
        className={`box-border content-stretch flex items-center py-[8px] relative rounded-lg shrink-0 w-full cursor-pointer transition-colors ${
          isSelected ? 'bg-[rgba(0,0,0,0.06)]' : 'hover:bg-[rgba(0,0,0,0.02)]'
        }`}
        style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '16px' }}
        onClick={handleClick}
      >
        {/* Icon Button */}
        <div className='box-border content-stretch flex items-center justify-center overflow-clip p-[4px] relative rounded-[4px] shrink-0 size-[32px]'>
          {isFolder ? <FolderIcon /> : <DocumentIcon selected={isSelected} />}
        </div>

        {/* File/Folder Name */}
        <p className="[white-space-collapse:collapse] basis-0 font-['Geist'] font-medium grow leading-[22px] min-h-px min-w-px overflow-ellipsis overflow-hidden relative shrink-0 text-[14px] text-[rgba(0,0,0,0.88)] text-nowrap">
          {node.name}
        </p>
      </div>

      {/* Render children if folder is expanded */}
      {isFolder && isExpanded && node.children && node.children.length > 0 && (
        <div className='content-stretch flex flex-col gap-[8px] items-start relative w-full'>
          {node.children.map((child, index) => (
            <FileTreeItem
              key={`${child.path}-${index}`}
              node={child}
              level={level + 1}
              selectedPath={selectedPath}
              onFileClick={onFileClick}
              onFolderToggle={onFolderToggle}
            />
          ))}
        </div>
      )}
    </>
  )
}
